import React, { useState } from 'react';

interface TrellusDialerButtonProps {
  variant?: 'minimal' | 'expanded';
  mode?: 'dialer' | 'close';
  dialerType?: 'Parallel' | 'Power';
  isExpired?: boolean;
}

const TrellusLogo = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg 
    height="28" 
    viewBox="0 0 40 50" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={style}
  >
    <path fillRule="evenodd" clipRule="evenodd" d="M26.2535 16.0368C26.2548 17.2236 26.2535 18.3853 26.2535 19.5721C28.7916 19.85 31.3296 20.1382 33.8677 20.4157C33.8698 22.7226 33.8677 21.2268 33.872 23.8423C33.8724 24.6006 33.3093 25.3524 32.4476 25.3325C29.8827 25.2658 27.3161 25.2104 24.7503 25.1507C24.7387 21.0536 24.7425 16.9557 24.7378 12.8591C24.7365 9.47312 21.4665 6.47671 18.2096 5.51888C12.1237 3.7283 6.08501 1.79317 0 0C0 2.93538 0 5.87119 0 8.80657C0 14.1692 5.67556 17.4474 10.7655 18.0126C14.0433 18.3762 17.3275 18.5909 20.6048 18.9458C20.6061 20.9826 20.6083 23.0186 20.6091 25.055C19.3241 25.0256 18.0395 24.9962 16.7553 24.9663C12.7803 24.8707 8.37332 27.5559 8.37419 31.5906C8.37722 37.4471 8.38284 43.304 8.38284 49.1609C11.6675 48.1979 14.9535 47.2323 18.2377 46.2671C21.4847 45.3149 24.7646 42.3085 24.7607 38.9325C24.7603 37.2125 24.7573 35.164 24.756 33.4444C24.7555 32.4121 24.7547 31.3798 24.7547 30.3484C23.374 30.4168 21.9924 30.3796 20.6117 30.4475C20.6117 33.2202 20.6173 36.3235 20.6173 39.0966C20.6173 40.5262 19.5578 42.0029 18.0408 42.376C16.654 42.718 15.2677 43.059 13.8818 43.4005C13.8814 39.3688 13.8758 35.3362 13.874 31.305C13.8732 29.9264 15.4053 28.9781 16.7562 28.9482C21.9868 28.8201 27.2191 28.6938 32.4498 28.5674C35.0294 28.5072 36.7836 26.2998 36.7801 23.9708C36.7771 21.8833 36.7767 19.7937 36.7715 17.7062C33.2656 17.1522 29.7602 16.5895 26.2535 16.0368ZM10.8646 13.5953C8.57458 13.2326 6.99393 12.086 6.99393 9.77479C6.99393 8.74814 6.99349 7.72193 6.99349 6.69615C10.6681 7.60117 14.341 8.50879 18.0148 9.41425C19.2557 9.72026 20.5979 11.0135 20.5983 12.2981C20.6018 13.3005 20.6018 14.1385 20.6026 15.14C17.3569 14.625 14.1103 14.1099 10.8646 13.5953Z" fill="currentColor"/>
  </svg>
);

export function TrellusDialerButton({ 
  variant = 'expanded', 
  mode = 'dialer',
  dialerType = 'Parallel',
  isExpired = false 
}: TrellusDialerButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const getBackgroundColor = () => {
    if (mode === 'close') {
      return isHovered ? '#dc2626' : '#ef4444'; // red-600 : red-500
    } else {
      return isHovered ? '#2563eb' : '#3b82f6'; // blue-600 : blue-500
    }
  };

  const getText = () => {
    if (isExpired) return 'Upgrade Power Dialer';
    if (mode === 'close') return 'Close Dialer';
    return `${dialerType} Dial Tasks`;
  };

  const getTransform = () => {
    if (isPressed) return 'translateY(0)';
    if (isHovered) return 'translateY(-1px)';
    return 'translateY(0)';
  };

  const getBoxShadow = () => {
    if (isPressed) return '0 2px 4px rgba(0, 0, 0, 0.1)';
    if (isHovered) return '0 4px 12px rgba(0, 0, 0, 0.15)';
    return 'none';
  };

  if (variant === 'minimal') {
    return (
      <div className="flex">
        <div
          className="flex items-center justify-center cursor-pointer rounded border transition-all duration-200 select-none"
          style={{
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            background: isHovered ? '#f3f4f6' : '#f8f9fa',
            borderColor: isHovered ? '#d1d5db' : '#e5e7eb',
            transform: isPressed ? 'scale(0.95)' : 'scale(1)',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
        >
          <TrellusLogo 
            style={{
              width: '20px',
              height: '20px',
              minWidth: '20px',
              minHeight: '20px',
              color: '#4f46e5'
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <div
        className="flex items-center rounded cursor-pointer select-none transition-all duration-200 border-none font-medium text-white"
        style={{
          padding: '8px 12px',
          fontSize: '13px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          background: getBackgroundColor(),
          transform: getTransform(),
          boxShadow: getBoxShadow(),
          gap: '8px'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
      >
        {/* Trellus Logo */}
        <TrellusLogo 
          style={{
            width: '16px',
            height: '16px',
            minWidth: '16px',
            color: 'white'
          }}
        />
        
        {/* Text and Action Icon Container */}
        <div className="flex items-center flex-grow" style={{ gap: '4px' }}>
          <span 
            className="whitespace-nowrap flex items-center"
            style={{ textTransform: 'none' }}
          >
            {getText()}
          </span>
          
          {/* Play Icon - only show in dialer mode */}
          {mode === 'dialer' && (
            <div className="flex items-center justify-center" style={{ height: '12px', width: '12px', minWidth: '12px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}